import React, { useState, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, List, Eye, EyeOff, MapPin, Navigation, Copy, Check, Shield, Zap } from 'lucide-react';
import { Business } from '../types';

const BusinessCardSkeleton = () => (
    <div className="p-3 rounded-lg bg-gray-800/50 border-l-4 border-gray-700 animate-pulse">
        <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 space-y-2">
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-700 rounded w-1/2"></div>
            </div>
            <div className="w-10 h-4 bg-gray-700 rounded-full"></div>
        </div>
    </div>
);

// Memoize the BusinessCard component to prevent unnecessary re-renders if its props don't change.
const BusinessCard = React.memo(({ business, isSelected, onClick, cardRef }: { business: Business, isSelected: boolean, onClick: () => void, cardRef: React.RefObject<HTMLDivElement> | null }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (business.address) {
        navigator.clipboard.writeText(business.address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    };
    const handleDirections = (e: React.MouseEvent) => {
      e.stopPropagation();
      window.open(`https://maps.google.com/?q=${business.coordinates.lat},${business.coordinates.lng}`, '_blank');
    };
    return (
        <div ref={cardRef} onClick={onClick} className={`relative p-3 rounded-lg border-l-4 transition-colors duration-200 cursor-pointer overflow-hidden group ${ isSelected ? 'bg-orange-500/10 border-orange-500' : 'bg-gray-800/50 border-gray-700 hover:bg-gray-700/50 hover:border-orange-600'}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white truncate text-sm group-hover:text-orange-300">{business.name}</h4>
                    <p className="text-xs text-gray-400 capitalize truncate">{business.category}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1 text-orange-400 font-bold text-xs"><span>ⓜ</span><span>XMR</span></div>
            </div>
            {business.address && (<div className="mt-2 flex items-start gap-2 text-gray-400"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-500" /><p className="text-xs flex-1 line-clamp-1">{business.address}</p></div>)}
            {isSelected && (
                <div className="mt-3 pt-3 border-t border-gray-700/50 space-y-2">
                    <div className="flex flex-wrap gap-1">
                        <button onClick={handleDirections} className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded-full hover:bg-blue-500/40 transition-colors"><Navigation className="w-3 h-3" /> Directions</button>
                        <button onClick={handleCopy} className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600/50 text-gray-300 rounded-full hover:bg-gray-600 transition-colors">{copied ? <><Check className="w-3 h-3 text-green-400" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}</button>
                    </div>
                    <div className="flex items-center gap-1"><Shield className="w-3 h-3 text-orange-500" /><span className="text-xs font-medium text-gray-200">Payment Methods</span></div>
                    <div className="flex flex-wrap gap-1">
                        {business.xmrPaymentMethods.map((method) => (<span key={method} className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs rounded-full"><Zap className="w-2 h-2" /> {method}</span>))}
                    </div>
                </div>
            )}
        </div>
    );
});

export const BusinessListOverlay: React.FC<{
  businesses: Business[];
  selectedBusiness: Business | null;
  onBusinessSelect: (business: Business) => void;
  loading?: boolean;
}> = ({ businesses, selectedBusiness, onBusinessSelect, loading = false }) => {

  const [isMinimized, setIsMinimized] = useState(false);
  const [isHidden, setIsHidden] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedCardRef = useRef<HTMLDivElement>(null);
  
  // Internal state to hold businesses, preventing the list from flashing if the parent sends an empty array during a refresh.
  const [displayedBusinesses, setDisplayedBusinesses] = useState<Business[]>([]);

  useEffect(() => {
    // We update our internal list only when we get a new list with content,
    // or if the list we are showing is empty.
    if (businesses.length > 0 || displayedBusinesses.length === 0) {
        setDisplayedBusinesses(businesses);
    }
  }, [businesses, displayedBusinesses.length]);

  // Effect to scroll the selected card into view smoothly and less aggressively.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const element = selectedCardRef.current;

    if (container && element) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }
  }, [selectedBusiness]);


  if (isHidden) {
    return (
      <div className="absolute top-4 right-4 z-[1000]">
        <button
          onClick={() => setIsHidden(false)}
          className="p-3 bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-lg text-white hover:bg-gray-800/95 transition-all shadow-lg"
          title="Show business list"
        >
          <Eye className="w-5 h-5" />
        </button>
      </div>
    );
  }

  // Show skeleton only on the very first load when we have no data at all.
  const showSkeleton = loading && displayedBusinesses.length === 0;
  // Use the new list from props if it has content, otherwise stick with the old one to prevent flashes.
  const businessesToRender = businesses.length > 0 ? businesses : displayedBusinesses;

  return (
    <div className={`
        absolute z-[1000] bg-gray-900/85 backdrop-blur-md border border-gray-700 shadow-2xl
        flex flex-col
        bottom-0 inset-x-0 h-[60%] rounded-t-xl
        transition-all duration-300 ease-in-out
        md:top-4 md:right-4 md:bottom-auto md:left-auto md:h-auto md:max-h-[calc(100%-2rem)] md:w-full md:max-w-sm md:rounded-xl
        ${isMinimized ? 'translate-y-[calc(100%-57px)]' : 'translate-y-0'}
        md:translate-y-0
    `}>
      <div
        className="flex-shrink-0 flex items-center justify-between p-3 border-b border-gray-700 cursor-pointer touch-action-none"
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <div className="flex items-center gap-2">
          <List className="w-4 h-4 text-orange-400" />
          <span className="text-white font-medium text-sm">
            Businesses ({showSkeleton ? '...' : businessesToRender.length})
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-1 text-gray-400 hover:text-white transition-colors" title={isMinimized ? "Expand" : "Minimize"}>
            {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); setIsHidden(true); }} className="p-1 text-gray-400 hover:text-white transition-colors" title="Hide list">
            <EyeOff className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className={`flex-1 overflow-y-auto overscroll-contain transition-all duration-300 ease-in-out ${isMinimized ? 'max-h-0 opacity-0' : 'max-h-full opacity-100'}`}
      >
          <div className="p-2 space-y-2">
              {showSkeleton ? (
                  Array.from({ length: 5 }).map((_, i) => <BusinessCardSkeleton key={i} />)
              ) : businessesToRender.length > 0 ? (
                  businessesToRender.map((business) => (
                      <BusinessCard
                          key={business.id}
                          business={business}
                          isSelected={selectedBusiness?.id === business.id}
                          onClick={() => onBusinessSelect(business)}
                          cardRef={selectedBusiness?.id === business.id ? selectedCardRef : null}
                      />
                  ))
              ) : (
                <div className="text-center py-10 px-4">
                    <p className="text-gray-400">No businesses found in this area.</p>
                    <p className="text-xs text-gray-500 mt-1">Try zooming out or moving the map.</p>
                </div>
              )}
          </div>
      </div>
    </div>
  );
};
